import { useMemo } from 'react';
import { useBasket } from '~/features/basket/hooks';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useUtils } from '~/shared/hooks/useUtils';
import { CustomerInformationInputs } from '../models/fields';

export const useSubmit = () => {
    const customer = useCustomer();
    const basket = useBasket();
    const { getCountryCode } = useUtils();

    const handlers = useMemo(
        () => ({
            submitInformation: async (
                values: CustomerInformationInputs,
                useAsShipping?: boolean,
            ) => {
                if (values.Consent) {
                    await basket.updateCustomerInfo({
                        email: values.email,
                        c_newsletter: values.NewsLetterSignup,
                    });

                    return basket.setBillingAddress(
                        {
                            address1: values.address1,
                            city: values.city,
                            firstName: values.firstName,
                            lastName: values.lastName,
                            fullName: `${values.firstName} ${values.lastName}`,
                            phone: String(values.phoneMobile),
                            postalCode: values.postalCode,
                            countryCode: getCountryCode(),
                        },
                        { useAsShipping },
                    );
                }
            },
            createAccount: async (values: CustomerInformationInputs) => {
                const data = {
                    customer: {
                        firstName: values.firstName,
                        lastName: values.lastName,
                        email: values.email ?? '',
                        login: values.email ?? '',
                        phone: values.phoneMobile ?? '',
                    },
                    password: values.password,
                };

                await customer.registerCustomer(data);

                return await customer.updateCustomer({
                    customerId: customer.customerId,
                    email: values.email ?? '',
                    firstName: values.firstName,
                    lastName: values.lastName,
                    fullName: `${values.firstName} ${values.lastName}`,
                    phoneMobile: String(values.phoneMobile),
                    postalCode: values.postalCode,
                    c_acceptedTermsAndConditions: values.createAccountConsent,
                    c_completeRegistration: true,
                    /**
                     * The signup terms says that the user automatically
                     * accepts marketing
                     */
                    c_newsletter: true,
                    c_socialMedia: true,
                    addresses: [
                        {
                            addressId: 'main',
                            countryCode: getCountryCode(),
                            firstName: values.firstName,
                            lastName: values.lastName,
                            address1: values.address1,
                            postalCode: values.postalCode,
                            city: values.city,
                            phone: String(values.phoneMobile),
                        },
                    ],
                });
            },
            submitShippingAddress: (values: Record<string, unknown>, shipmentId = '') => {
                return basket.setShippingAddress(
                    {
                        address1: values.address1,
                        city: values.city,
                        firstName: values.firstName,
                        lastName: values.lastName,
                        fullName: `${values.firstName} ${values.lastName}`,
                        phone: String(values.phoneMobile ?? values.phone),
                        postalCode: values.postalCode,
                        countryCode: getCountryCode(),
                    },
                    shipmentId,
                );
            },
        }),
        [],
    );

    return {
        handlers,
    };
};
